import { FC, ReactNode, useMemo, useState } from 'react';
import { Input, Modal, Select, Table, Typography } from '@crisellevaldez/speculo-ui';
import { Activity, Search } from 'lucide-react';
import { TableCard } from '../../common/TableCard';
import { OffsetPagination } from '../../common/OffsetPagination';
import { BackButton } from '../../common/BackButton';
import { OutcomeBadge } from '../../common/OutcomeBadge';
import { ChannelBadge } from './ChannelBadge';
import { CallDetailsPanel } from '../contacts/CallDetailsPanel';
import { ContactHeader } from '../calls/ContactHeader';
import { EditContactModal } from '../contacts/EditContactModal';
import { EditCallAnalysisModal } from '../contacts/EditCallAnalysisModal';
import { SmsThreadView } from '../dashboard/SmsThreadModal';
import { EmailThreadView } from '../dashboard/EmailThreadModal';
import { formatNumber } from '../../../utils/format';
import { useDebounce } from '../../../hooks/useDebounce';
import { formatDate } from '../../../utils/dateTime';
import { formatDurationFromSeconds } from '../../../utils/duration';
import { formatPhoneNumber } from '../../../utils/formatters';
import { useFlywheelEnabled, useOrganizationStore } from '../../../stores/useOrganizationStore';
import {
  FLOW_ACTIVITY_PAGE_SIZE,
  useFlowActivityGQL,
  type FlowActivityItem,
  type FlowThread,
} from '../../../hooks/graphql/flows/useFlowActivityGQL';
import { activeFlowActivityChannels, type FlowActivityChannel } from './flowActivityChannels';
import { flowActivityTotal } from './flowActivity';
import { flowSmsAnchor, flowSmsLookup } from './flowSmsThread';
import { useOrgSmsMessagesGQL } from '../../../hooks/graphql/dashboard/useOrgSmsMessagesGQL';

// The panel looks a thread up by id, not by date.
const NO_DATE_RANGE = { from: null, to: null };

type ActivityFilter = 'all' | FlowActivityChannel;
type Row = FlowActivityItem & Record<string, unknown>;

const CHANNEL_LABELS: Record<FlowActivityChannel, string> = {
  call: 'Calls',
  sms: 'SMS',
  email: 'Email',
};

const TYPE_LABELS: Record<FlowActivityChannel, string> = {
  call: 'Call',
  sms: 'SMS',
  email: 'Email',
};

function TypeCell({ item }: { item: FlowActivityItem }) {
  return <ChannelBadge channel={item.channel}>{TYPE_LABELS[item.channel]}</ChannelBadge>;
}

function NameCell({
  item,
  onOpenContact,
}: {
  item: FlowActivityItem;
  onOpenContact: (contactId: string) => void;
}) {
  const contactId = item.contact.id;
  const name = (item.channel === 'call' ? item.data.fullName : item.data.contactName) || '-';
  if (!contactId || name === '-') return <span className="text-muted-foreground">{name}</span>;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onOpenContact(contactId);
      }}
      className="text-primaryfortext font-medium underline text-left"
    >
      {name}
    </button>
  );
}

/** Phone over email, as in the Contacts page's Contact column. */
function ContactCell({ item }: { item: FlowActivityItem }) {
  const { phone, email } = item.contact;
  if (!phone && !email) return <span className="text-muted-foreground">-</span>;
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      {phone && (
        <a
          href={`tel:${phone}`}
          onClick={(e) => e.stopPropagation()}
          className="text-foreground hover:text-primary hover:underline transition-colors w-fit"
        >
          {formatPhoneNumber(phone)}
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          onClick={(e) => e.stopPropagation()}
          className="t-caption truncate hover:text-primary hover:underline transition-colors"
        >
          {email}
        </a>
      )}
    </div>
  );
}

function DetailsCell({ item }: { item: FlowActivityItem }) {
  if (item.channel === 'call') {
    const outcome = item.data.call_analysis?.outcome;
    const seconds = item.data.corrected_duration || item.data.duration;
    if (!outcome && !seconds) return <span className="text-muted-foreground">-</span>;
    return (
      <span className="inline-flex items-center gap-2">
        {outcome ? <OutcomeBadge outcome={outcome} /> : null}
        {seconds ? (
          <span className="tabular-nums text-muted-foreground">
            {formatDurationFromSeconds(seconds)}
          </span>
        ) : null}
      </span>
    );
  }
  const n = item.data.messageCount;
  const noun = item.channel === 'email' ? 'email' : 'message';
  const subject = item.channel === 'email' ? item.data.lastMessageSubject : null;
  const line = subject || item.data.lastMessagePreview;
  return (
    <div className="min-w-0">
      <div className="tabular-nums">
        {formatNumber(n)} {n === 1 ? noun : `${noun}s`}
      </div>
      {line && (
        <div className="truncate text-xs text-muted-foreground" title={line}>
          {line}
        </div>
      )}
    </div>
  );
}

/** A thread view laid out in the side panel instead of a modal. */
const panelFrame = (header: ReactNode, body: ReactNode) => (
  <div className="flex flex-col h-full min-h-0">
    <div className="px-4 py-3 border-b border-border shrink-0">{header}</div>
    <div className="flex-1 min-h-0 flex flex-col px-3 py-2">{body}</div>
  </div>
);

/**
 * An SMS thread in the panel, opened at its newest message and paged older and
 * newer from there. The message is read in full first: the view shows the
 * anchor's body as-is, and the table's preview is trimmed.
 */
const FlowSmsThread: FC<{
  thread: FlowThread;
  contactPhone: string | null;
  onContactClick: (contactId: string) => void;
}> = ({ thread, contactPhone, onContactClick }) => {
  const latest = useOrgSmsMessagesGQL({
    dateRange: NO_DATE_RANGE,
    ...flowSmsLookup(thread),
    sortOrder: 'DESC',
  });
  const message = latest.items[0];
  if (!message) {
    return (
      <div className="h-full flex items-center justify-center px-6 text-sm text-muted-foreground">
        {latest.isLoading ? 'Loading conversation…' : 'No messages in this thread.'}
      </div>
    );
  }
  return (
    <SmsThreadView
      active
      contactId={thread.contactId}
      contactName={thread.contactName}
      contactPhone={contactPhone}
      conversationId={thread.conversationId}
      anchor={flowSmsAnchor(message)}
      onContactClick={onContactClick}
    >
      {panelFrame}
    </SmsThreadView>
  );
};

interface FlowActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  calls?: number;
  sms?: number;
  email?: number;
}

/**
 * Everything a flow sent (calls, SMS and email threads) in one table, newest
 * first. Selecting a row shows the call breakdown or the conversation beside it.
 */
export const FlowActivityModal: FC<FlowActivityModalProps> = ({
  isOpen,
  onClose,
  flowId,
  flowName,
  calls,
  sms,
  email,
}) => {
  const flywheelEnabled = useFlywheelEnabled();
  const { organization } = useOrganizationStore();
  const timezone = organization?.timezone || 'UTC';
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const channels = useMemo(
    () => activeFlowActivityChannels(filter === 'all' ? [] : [filter], flywheelEnabled),
    [filter, flywheelEnabled]
  );

  const {
    items,
    total: rowTotal,
    hasMore,
    isLoading,
  } = useFlowActivityGQL({
    organizationId: organization?.id ?? '',
    flowId,
    channels,
    page,
    search,
    enabled: isOpen,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showInfoOnMobile, setShowInfoOnMobile] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [editOutcomeCallId, setEditOutcomeCallId] = useState<string | null>(null);
  // An edited outcome shows at once; the feed's cached page still holds the old one.
  const [outcomeEdits, setOutcomeEdits] = useState<Record<string, string>>({});

  const rows = useMemo(
    () =>
      items.map((item) => {
        const edited = item.channel === 'call' ? outcomeEdits[item.data.call_id] : undefined;
        if (item.channel !== 'call' || edited === undefined) return item;
        return {
          ...item,
          data: { ...item.data, call_analysis: { ...item.data.call_analysis, outcome: edited } },
        } as FlowActivityItem;
      }) as Row[],
    [items, outcomeEdits]
  );
  const selected = rows.find((r) => r.key === selectedKey) ?? null;

  const counts: Record<FlowActivityChannel, number> = {
    call: calls ?? 0,
    sms: flywheelEnabled ? (sms ?? 0) : 0,
    email: email ?? 0,
  };
  const total = flowActivityTotal({ calls: counts.call, sms: counts.sms, email: counts.email });
  // A channel the flow never used has nothing to filter to.
  const filterOptions: { value: ActivityFilter; label: string }[] = [
    { value: 'all', label: total > 0 ? `All activity (${formatNumber(total)})` : 'All activity' },
    ...activeFlowActivityChannels([], flywheelEnabled)
      .filter((c) => counts[c] > 0)
      .map((c) => ({ value: c, label: `${CHANNEL_LABELS[c]} (${formatNumber(counts[c])})` })),
  ];

  const clearSelection = () => {
    setSelectedKey(null);
    setShowInfoOnMobile(false);
  };

  const goToPage = (next: number) => {
    setPage(next);
    clearSelection();
  };

  const handleClose = () => {
    setFilter('all');
    setSearchInput('');
    setPage(0);
    clearSelection();
    onClose();
  };

  const renderDetails = () => {
    if (!selected) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-center px-6">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
            <Activity className="w-5 h-5 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            Select an activity to view details
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Calls show their breakdown; SMS and email show the conversation
          </p>
        </div>
      );
    }
    if (selected.channel === 'call') {
      const call = selected.data;
      // Same header as the Calls pop-up (and the SMS / email views): name, then phone.
      return panelFrame(
        <ContactHeader
          name={call.fullName && call.fullName !== '-' ? call.fullName : null}
          subtitle={selected.contact.phone ? formatPhoneNumber(selected.contact.phone) : null}
          contactId={selected.contact.id}
          onContactClick={setSelectedContactId}
          fallback="Call Details"
        />,
        <CallDetailsPanel
          call={call}
          orgTimezone={timezone}
          flowInfo={
            call.flowName && call.flowName !== '-'
              ? { name: call.flowName, type: call.flowType || '-' }
              : undefined
          }
          assistantName={organization?.assistant_name as string | undefined}
          onEditOutcome={() => setEditOutcomeCallId(call.call_id || call.id)}
        />
      );
    }
    const thread = selected.data;
    const shared = {
      active: isOpen,
      contactId: thread.contactId,
      contactName: thread.contactName,
      conversationId: thread.conversationId,
      onContactClick: setSelectedContactId,
    };
    return selected.channel === 'sms' ? (
      <FlowSmsThread
        key={selected.key}
        thread={thread}
        contactPhone={selected.contact.phone}
        onContactClick={setSelectedContactId}
      />
    ) : (
      <EmailThreadView
        key={selected.key}
        {...shared}
        contactEmail={selected.contact.email}
        // A message sent outside any conversation is its own thread, keyed by its id.
        anchorMessageId={thread.conversationId ? null : thread.threadId}
        // A flow's own emails are template sends; list them with any Remi replies.
        emailKinds={null}
      >
        {panelFrame}
      </EmailThreadView>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="full" className="mx-0 h-full">
        <Modal.Header>
          <div className="flex flex-col gap-0.5">
            <Typography variant="h2">{flowName}</Typography>
            {total > 0 && (
              <div className="text-xs text-muted-foreground">
                {formatNumber(total)} {total === 1 ? 'activity' : 'activities'}
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="flex-1 flex flex-col min-h-0">
          <div className="flex flex-1 min-h-0 gap-4 overflow-hidden md:flex-row flex-col">
            <div
              // p-1 keeps the search and filter focus rings inside the row's clipped overflow.
              className={`md:w-[60%] w-full flex flex-col min-h-0 p-1 ${showInfoOnMobile ? 'hidden md:flex' : ''}`}
            >
              <div className="mb-4 shrink-0 flex flex-col sm:flex-row sm:items-center gap-2 w-full">
                <div className="flex-1 min-w-0">
                  <Input
                    placeholder="Search by name, email or phone..."
                    startIcon={<Search className="text-muted-foreground" size={16} />}
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      goToPage(0);
                    }}
                    className="w-full"
                  />
                </div>
                <div className="w-full sm:w-[220px] shrink-0">
                  <Select
                    options={filterOptions}
                    value={filter}
                    onChange={(value) => {
                      const v = (Array.isArray(value) ? value[0] : value) as
                        ActivityFilter | undefined;
                      setFilter(v || 'all');
                      goToPage(0);
                    }}
                    placeholder="All activity"
                  />
                </div>
              </div>
              <div className="flex flex-col flex-1 min-h-0 [&_tbody_tr]:cursor-pointer">
                <TableCard
                  className="flex-1 min-h-0"
                  footer={
                    <OffsetPagination
                      currentPage={page}
                      totalItems={rowTotal}
                      pageSize={FLOW_ACTIVITY_PAGE_SIZE}
                      onNextPage={() => goToPage(page + 1)}
                      onPrevPage={() => goToPage(Math.max(0, page - 1))}
                      onPageChange={goToPage}
                      hasMore={hasMore}
                      isLoading={isLoading}
                      label="rows"
                    />
                  }
                >
                  <Table<Row>
                    columns={[
                      {
                        key: 'channel',
                        header: 'Type',
                        width: '100px',
                        cell: (item) => <TypeCell item={item} />,
                      },
                      {
                        key: 'name',
                        header: 'Full Name',
                        width: '180px',
                        cell: (item) => (
                          <NameCell item={item} onOpenContact={setSelectedContactId} />
                        ),
                      },
                      {
                        key: 'contact',
                        header: 'Contact',
                        width: '220px',
                        cell: (item) => <ContactCell item={item} />,
                      },
                      {
                        key: 'details',
                        header: 'Details',
                        width: '260px',
                        cell: (item) => <DetailsCell item={item} />,
                      },
                      {
                        key: 'at',
                        header: 'Date/Time',
                        width: '190px',
                        cell: (item) =>
                          item.at ? (
                            <span className="whitespace-nowrap">
                              {formatDate(item.at, timezone)}
                            </span>
                          ) : (
                            '-'
                          ),
                      },
                    ]}
                    data={rows}
                    keyExtractor={(item) => item.key}
                    loading={isLoading}
                    emptyStateText={
                      search.trim()
                        ? 'No activity matches your search'
                        : 'No activity for this flow'
                    }
                    rowSelectable
                    selectedRowId={selectedKey ?? undefined}
                    onRowSelect={(key) => {
                      setSelectedKey(key ?? null);
                      setShowInfoOnMobile(!!key);
                    }}
                    className="w-full"
                    size="xl"
                  />
                </TableCard>
              </div>
            </div>
            <div
              className={`md:w-[40%] w-full md:h-auto md:my-1 min-h-0 max-h-[60vh] md:max-h-none rounded-xl border border-border bg-muted/30 overflow-y-auto scrollbar-custom ${!showInfoOnMobile && !selected ? 'hidden md:block' : ''}`}
            >
              {showInfoOnMobile && (
                <div className="p-4 border-b border-border md:hidden">
                  <BackButton onClick={clearSelection} />
                </div>
              )}
              {renderDetails()}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {selectedContactId && (
        <EditContactModal
          isOpen={!!selectedContactId}
          onClose={() => setSelectedContactId(null)}
          onSubmit={async () => {}}
          contact={{ id: selectedContactId }}
        />
      )}

      {editOutcomeCallId && (
        <EditCallAnalysisModal
          isOpen={!!editOutcomeCallId}
          onClose={() => setEditOutcomeCallId(null)}
          callId={editOutcomeCallId}
          currentOutcome={
            selected?.channel === 'call' ? selected.data.call_analysis?.outcome || '' : ''
          }
          onSuccess={(newOutcome) =>
            setOutcomeEdits((prev) => ({ ...prev, [editOutcomeCallId]: newOutcome }))
          }
        />
      )}
    </>
  );
};
